'use client';

import {AnalyticEventProps} from '@/types';
import {useCallback, useEffect} from 'react';
import {UAParser} from 'ua-parser-js';
import {isBrowser} from './page.utils';
import {getElapsedTimeInfo} from './date.utils';
import {getCookieValue} from '@/hooks';

const isQRCodeSuspected = (url: string): boolean => {
	try {
		const parsed = new URL(url);
		if (parsed.search || parsed.hash) return false;
		const segments = parsed.pathname.split('/').filter(Boolean);
		return segments.length === 2;
	} catch {
		return false;
	}
};

export const setSessionSource = () => {
	if (!isBrowser()) return;
	try {
		const searchParams = new URLSearchParams(location.search);
		const source = searchParams.get('source');
		sessionStorage.setItem(
			'source',
			encodeURIComponent(
				source || isQRCodeSuspected(location.href) ? 'qr_code_suspected' : 'normal'
			).toLowerCase()
		);
	} catch {
		console.warn('unable to determine session source');
		return undefined;
	}
};

const getSessionSource = () => {
	if (!isBrowser()) return;
	try {
		return encodeURIComponent(sessionStorage.getItem('source')!);
	} catch {
		return undefined;
	}
};

export const setSessionTimestamp = () => {
	if (!isBrowser()) return;
	sessionStorage.setItem('sessionTimestamp', Date.now().toString());
};

const getSessionTimestamp = () => {
	if (!isBrowser()) return;
	const value = sessionStorage.getItem('sessionTimestamp');
	try {
		const date = new Date(Number(value));
		return date.toString();
	} catch {
		return undefined;
	}
};

const defaultEventValues = () => {
	const toReturn: Record<string, string> = {
		owner: process.env.NEXT_PUBLIC_NAV_OWNER || 'fgc',
		app: process.env.NEXT_PUBLIC_APP || 'Fortnite Gift Card',
		loggedIn: _epicTrackingAuthenticated || 'false',
		mainView: 'Gift Card Redemption'
	};

	const {search, hash, href} = location || {};
	if (search) toReturn.pageParams = search;
	if (hash) toReturn.pageHash = hash;
	if (href) toReturn.pageUrl = href;
	if (_epicTrackingAccountCountry) toReturn.country = _epicTrackingAccountCountry;
	if (_epicTrackingCountryCode) toReturn.geo = _epicTrackingCountryCode;

	try {
		const timestamp = getSessionTimestamp()!;
		const sessionDuration = getElapsedTimeInfo(timestamp).seconds;
		if (sessionDuration) {
			toReturn.sessionElapsedTimeInSeconds = String(sessionDuration);
		}
	} catch {
		console.warn('unable to determine session duration');
	}

	try {
		const source = getSessionSource()!;
		if (source) toReturn.source = String(source);
	} catch {
		console.warn('unable to determine session source');
	}

	return toReturn;
};

export const emitEvent = (
	analyticsEvent: AnalyticEventProps,
	type: 'Interaction' | 'PageView' = 'Interaction'
): void => {
	if (!analyticsEvent) {
		console.error('Failed to send analytic event analyticsEvent is invalid');
		return;
	}

	if (
		typeof _epicTracking !== 'undefined' &&
		_epicTracking &&
		typeof _epicTracking.trackEvent === 'function'
	) {
		try {
			const event = {
				...analyticsEvent,
				application: process.env.NEXT_PUBLIC_TRACKING_NAME || 'EPICWEBTRACKING',
				appName: process.env.NEXT_PUBLIC_APP ?? 'fgc',
				appEnv: process.env.NEXT_PUBLIC_ENV,
				// appBranch: "UNKNOWN",
				appVersion: encodeURIComponent(window._epicAppVersion) || 'UNKNOWN',
				// appBuild: "UNKNOWN",
				trackingUUID: getCookieValue('_epicSID'),
				eventDate: new Date().toISOString(), //a dateTime string in which the event occurs
				eventValue: JSON.stringify({
					...defaultEventValues(),
					...analyticsEvent?.eventValue
				}),
				userAgent: JSON.stringify(UAParser()).replace(/</g, '\\u003c')
			};

			_epicTracking.trackEvent(type, event);
			console.debug('[epic-tracking] emitted event', analyticsEvent.eventAction, event);
		} catch (e) {
			console.error('Failed to send analytic event', e);
		}
	} else {
		console.error('Failed to send analytic event _epicTracking is unavailable');
	}
};

type OptionalMouseEventHandler<T extends Element> = (
	event?: React.SyntheticEvent<T> | Event | null,
	overrideEventValue?: Record<string, any>
) => void;

export const onEmitClickEvent = <T extends Element>(
	analyticsEvent: AnalyticEventProps
): OptionalMouseEventHandler<T> => {
	return (clickEvent, overrideEventValue?: Record<string, any>) => {
		try {
			const eventProps: AnalyticEventProps = {
				eventCategory: analyticsEvent?.eventCategory || 'button',
				interactionType: analyticsEvent?.interactionType || 'click',
				eventAction: analyticsEvent?.eventAction || 'navigation',
				eventLabel: analyticsEvent?.eventLabel || '',
				eventValue: {
					...defaultEventValues(),
					...analyticsEvent?.eventValue,
					...overrideEventValue,
					component: analyticsEvent?.eventValue?.component || '',
					innerWidth: window.innerWidth,
					innerHeight: window.innerHeight
				}
			};

			// tbd, need to refactor analytics.ts eslint errors uglified this file
			if (clickEvent && 'clientX' in clickEvent) {
				const typedEvent = clickEvent as React.MouseEvent<T>;
				Object.assign(eventProps.eventValue!, {
					clientX: typedEvent.clientX,
					clientY: typedEvent.clientY,
					pageX: typedEvent.pageX,
					pageY: typedEvent.pageY,
					disabled: typedEvent.target?.['disabled']
				});
			}
			emitEvent(eventProps);
		} catch (err) {
			if (process.env.NODE_ENV === 'development') {
				console.error('Failed to emit click event', err);
			}
		}
	};
};

export const useEmitEvent = (analyticsEvent: AnalyticEventProps): (() => void) => {
	return useCallback(() => emitEvent(analyticsEvent), [analyticsEvent]);
};

export const useEmitEventOnClick = <T extends Element>(analyticsEvent: AnalyticEventProps) => {
	return useCallback(onEmitClickEvent<T>(analyticsEvent), [analyticsEvent]);
};

export const useEmitPageEvent = (analyticsEvent: Partial<AnalyticEventProps>) => {
	const pageUrl = isBrowser() ? location?.href : null;

	const trackingEnabled = Boolean(
		(!process.env.NEXT_PUBLIC_TRACKING_DISABLED ||
			process.env.NEXT_PUBLIC_TRACKING_DISABLED === 'false') &&
			process.env.NEXT_PUBLIC_TRACKING_BASE
	);

	useEffect(() => {
		if (!trackingEnabled || !pageUrl) return;
		// trackingjs sends its own page event but with less details
		// we'll fire another with more details
		emitEvent(
			{
				...analyticsEvent,
				eventCategory: 'pageview',
				interactionType: 'pageview',
				eventLabel: 'PageView_V2'
			},
			'PageView'
		);
	}, [trackingEnabled, pageUrl]);
};

/** Helper JSX to emit event - used for hygraph use-cases */
export const EmitPageEvent = (analyticsProps: Partial<AnalyticEventProps>) => {
	useEmitPageEvent(analyticsProps);
	return <></>;
};
